import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { GetPatient } from "../../../services/fhir/patient/GetPatient";
import { SearchCPF } from "../../../services/scim/signup/SearchCPF";
import { HTTPResponseStatus } from "../../../utils/enum";
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PetsIcon from "@mui/icons-material/Pets";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SearchIcon from "@mui/icons-material/Search";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { toast } from "react-toastify";
import { PostAdminQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/PostAdminQuestionnaireResponse";
import dayjs from "dayjs";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import { useFormatter } from "@/utils/useFormatter";
import { CircularLoading } from "@/components/common";
import { oids } from "@/configs/Settings";

/**
 * @description Interface para os dados do pet
 */
interface PetData {
  id: string | undefined;
  nome: string;
  especie: string;
  raca: string;
  idade?: number;
  peso?: string;
  fotoUrl?: string;
  ultimaConsulta?: string;
  observacoes?: string;
  birthDate?: string;
  gender?: string;
  color?: string;
  genderStatus?: string;
  city?: string;
  state?: string;
  active?: boolean;
  photo?: {
    data: string;
    contentType: string;
  };
}

/**
 * @description Interface para os dados do tutor
 */
interface TutorData {
  nome: string;
  cpf: string;
  telefone: string;
}

/**
 * @description Interface para os dados do visitante
 */
interface VisitanteData {
  cpf: string;
  telefone?: string;
  motivo: string;
  outroMotivo?: string;
  nome?: string;
}

/**
 * @description Interface para os dados do formulário
 */
interface FormData {
  cpf: string;
  telefone: string;
  motivoAcesso?: MotivoAcesso;
  outroMotivo: string;
}

/**
 * @description Tipos de motivo de acesso
 */
type MotivoAcesso = "consulta" | "encontrarPet" | "outro";

/**
 * @description Componente de acesso aos dados do pet mediante verificação de CPF
 * @returns {JSX.Element} Componente de acesso aos dados do pet
 */
export default function PetAccess() {
  const { id } = useParams<{ id: string }>();

  // Configuração do react-hook-form
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    mode: "onSubmit",
    defaultValues: {
      cpf: "",
      telefone: "",
      motivoAcesso: undefined,
      outroMotivo: "",
    },
  });

  // Observar valores do formulário
  const motivoAcesso = watch("motivoAcesso");

  const [petData, setPetData] = useState<PetData | null>(null);
  const [petLoading, setPetLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Estado para armazenar os dados do pet

  // Estado para armazenar os dados do tutor
  const [tutorData, setTutorData] = useState<TutorData | null>(null);

  /**
   * @description Verifica o CPF inserido usando GetPatient e SearchCPF
   * @param data Dados do formulário
   */
  const handleVerifyCpf = async (data: FormData) => {
    let verified = false;
    let nomeAcesso = "";
    try {
      setLoading(true);
      // Primeiro tenta buscar o paciente via GetPatient
      const patientResponse = await GetPatient({
        identifier: data.cpf,
        vetPatient: true,
        authType: "application",
      });

      if (
        patientResponse &&
        patientResponse.status === HTTPResponseStatus.Success
      ) {
        nomeAcesso = patientResponse?.data?.name?.[0]?.text;

        verified = true;
      }

      // Se encontrou o paciente no GetPatient
    } catch (error) {
      try {
        const cpfResponse = await SearchCPF(data.cpf, "application");

        const cpfData = cpfResponse?.data;

        nomeAcesso = cpfData?.nome;

        verified = true;
      } catch (error) {
        toast.error(
          "CPF não encontrado, por favor verifique se o CPF está correto e tente novamente."
        );
        verified = false;
      }
    } finally {
      if (verified) {
        toast.success("CPF validado com sucesso!");
        await PostAdminQuestionnaireResponse(
          {
            resourceType: "QuestionnaireResponse",
            subject: {
              reference: `Patient/${petData?.id}`,
            },
            source: {
              reference: `Patient/${oids.cpf}-${tutorData?.cpf}`,
            },
            questionnaire: "Questionnaire/petAccess",
            status: "completed",
            authored: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
            item: [
              {
                linkId: "cpfAcesso",
                text: "CPF de acesso",
                answer: [
                  {
                    valueString: data.cpf,
                  },
                ],
              },
              {
                linkId: "nomeAcesso",
                text: "nome de acesso",
                answer: [
                  {
                    valueString: nomeAcesso,
                  },
                ],
              },
              {
                linkId: "telefoneAcesso",
                text: "Telefone de acesso",
                answer: [
                  {
                    valueString: data.telefone,
                  },
                ],
              },
              {
                linkId: "nomePet",
                text: "Nome do Pet",
                answer: [
                  {
                    valueString: petData?.nome || "",
                  },
                ],
              },
              {
                linkId: "idPet",
                text: "ID do Pet",
                answer: [
                  {
                    valueString: petData?.id || "",
                  },
                ],
              },
              {
                linkId: "motivoAcesso",
                text: "Motivo de Acesso",
                answer: [
                  {
                    valueString: data.motivoAcesso,
                  },
                ],
              },
            ],
          },
          "application"
        );
      }
      setIsVerified(verified);
      setLoading(false);
    }
  };

  /**
   * @description Manipula a mudança do motivo de acesso
   */
  const handleMotivoChange = (event: SelectChangeEvent) => {
    setValue("motivoAcesso", event.target.value as MotivoAcesso);
  };

  useEffect(() => {
    const getPet = async () => {
      try {
        setPetLoading(true);
        // Busca o RelatedPerson (tutor) e o Patient (pet) relacionado
        const response = await GetRelatedPerson({
          patient: id, // ID do pet (Patient)
          includePatient: true, // Inclui os dados do pet
          includePhoto: true,
          vetRelated: true, // Importante para buscar dados veterinários
          authType: "application",
        });

        if (response?.data?.entry && response.data.entry.length > 0) {
          // Processa os dados da resposta
          const entries = response.data.entry;

          // Encontra o RelatedPerson (tutor)
          const relatedPersonEntry = entries.find(
            (entry: any) => entry.resource?.resourceType === "RelatedPerson"
          );

          // Encontra o Patient (pet)
          const patientEntry = entries.find(
            (entry: any) => entry.resource?.resourceType === "Patient"
          );

          if (relatedPersonEntry?.resource) {
            const relatedPerson = relatedPersonEntry.resource;

            // Extrai os dados do tutor
            const tutorInfo: TutorData = {
              nome: relatedPerson.name?.[0]?.text || "",
              cpf: relatedPerson.identifier?.[0]?.value || "",
              telefone: relatedPerson.telecom?.[0]?.value || "",
            };

            setTutorData(tutorInfo);
          }

          if (patientEntry?.resource) {
            const patient = patientEntry.resource;
            const extensions = patient.extension || [];

            // Encontra a extensão animal-patient
            const animalExtension = extensions.find(
              (ext: any) =>
                ext.url ===
                "http://hl7.org/fhir/StructureDefinition/patient-animal"
            );

            // Extrai dados da extensão animal
            let especie = "";
            let raca = "";
            let genderStatus = "";
            let color = "";

            if (animalExtension?.extension) {
              const animalExtensions = animalExtension.extension;

              // Espécie
              const speciesExt = animalExtensions.find(
                (ext: any) => ext.url === "species"
              );
              especie =
                speciesExt?.valueCodeableConcept?.coding?.[0]?.display || "";

              // Raça
              const breedExt = animalExtensions.find(
                (ext: any) => ext.url === "breed"
              );
              raca = breedExt?.valueCodeableConcept?.coding?.[0]?.display || "";

              // Status de gênero (castrado, etc)
              const genderStatusExt = animalExtensions.find(
                (ext: any) => ext.url === "genderStatus"
              );
              genderStatus =
                genderStatusExt?.valueCodeableConcept?.coding?.[0]?.display ||
                "";

              // Cor
              const colorExt = animalExtensions.find(
                (ext: any) => ext.url === "animalColor"
              );
              color =
                colorExt?.valueCodeableConcept?.coding?.[0]?.display || "";
            }

            // Calcula idade a partir da data de nascimento
            let idade;
            if (patient.birthDate) {
              const birthDate = new Date(patient.birthDate);
              const today = new Date();
              idade = today.getFullYear() - birthDate.getFullYear();
            }

            // Cria objeto com dados do pet
            const petInfo: PetData = {
              id: patient.id,
              nome: patient.name?.[0]?.text || "",
              especie,
              raca,
              idade,
              birthDate: patient.birthDate,
              gender: patient.gender,
              color,
              genderStatus,
              city: patient.address?.[0]?.city,
              state: patient.address?.[0]?.state,
              active: patient.active,
              // Extrai a foto do pet se disponível
              photo: patient.photo?.[0],
              // Dados que não temos na API, mantidos como undefined
              peso: undefined,
              ultimaConsulta: undefined,
              observacoes: undefined,
            };

            setPetData(petInfo);
          }
        }
        if (!response?.data?.entry || response.data.entry.length === 0) {
          throw new Error(
            "Não foi possível carregar os dados do pet. Tente novamente mais tarde."
          );
        }
      } catch (error) {
        setPetData(null);
      } finally {
        setPetLoading(false);
      }
    };

    if (id) {
      getPet();
    }
  }, [id]);

  if (petLoading) {
    return (
      <Stack p={3} height="100%" justifyContent="center" alignItems="center">
        <Paper
          elevation={3}
          sx={{
            p: 3,
            maxWidth: 500,
            width: "100%",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <CircularLoading />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Carregando...
          </Typography>
        </Paper>
      </Stack>
    );
  }
  if (!petLoading && !petData?.id) {
    return (
      <Stack p={3} height="100%" justifyContent="center" alignItems="center">
        <Paper
          elevation={3}
          sx={{
            p: 3,
            maxWidth: 500,
            width: "100%",
            borderRadius: 2,
          }}
        >
          <Alert
            severity="error"
            variant="outlined"
            sx={{
              mb: 2,
              alignItems: "center",
            }}
          >
            <AlertTitle>Erro ao carregar dados</AlertTitle>
            Não foi possível carregar os dados do pet. Tente novamente mais
            tarde.
          </Alert>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Tentar novamente
          </Button>
        </Paper>
      </Stack>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: { xs: 1, sm: 2 },
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          component="h1"
          gutterBottom
          align="center"
          sx={{ fontWeight: "medium" }}
        >
          Acesso aos Dados do Pet
        </Typography>

        {!isVerified ? (
          <Box
            sx={{
              maxWidth: 400,
              mx: "auto",
              mt: { xs: 2, sm: 3, md: 4 },
              width: "100%",
            }}
          >
            <Typography variant="body1" gutterBottom align="center">
              Para acessar os dados do pet, por favor preencha as informações
              abaixo:
            </Typography>

            <Controller
              name="cpf"
              control={control}
              rules={{
                required: "Por favor, insira um CPF válido",
                pattern: {
                  value: /^[0-9]{11}$|^[0-9]{3}\.[0-9]{3}\.[0-9]{3}-[0-9]{2}$/,
                  message: "CPF inválido",
                },
              }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <TextField
                  fullWidth
                  required
                  label="Seu CPF"
                  variant="outlined"
                  {...field}
                  error={!!fieldError}
                  helperText={fieldError ? fieldError.message : ""}
                  sx={{ mb: 2 }}
                  placeholder="000.000.000-00"
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              )}
            />

            <Controller
              name="telefone"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Seu Telefone (opcional)"
                  variant="outlined"
                  {...field}
                  sx={{ mb: 2 }}
                  placeholder="(00) 00000-0000"
                  InputProps={{
                    startAdornment: <PhoneIcon color="action" sx={{ mr: 1 }} />,
                  }}
                  inputProps={{
                    inputMode: "tel",
                  }}
                />
              )}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="motivo-acesso-label">Motivo do Acesso</InputLabel>
              <Controller
                name="motivoAcesso"
                control={control}
                render={({ field }) => (
                  <Select
                    labelId="motivo-acesso-label"
                    id="motivo-acesso"
                    label="Motivo do Acesso"
                    {...field}
                    onChange={handleMotivoChange}
                  >
                    <MenuItem value="consulta">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <MedicalServicesIcon sx={{ mr: 1 }} />
                        Consulta
                      </Box>
                    </MenuItem>
                    <MenuItem value="encontrarPet">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <SearchIcon sx={{ mr: 1 }} />
                        Pet encontrado
                      </Box>
                    </MenuItem>
                    <MenuItem value="outro">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <MoreHorizIcon sx={{ mr: 1 }} />
                        Outro
                      </Box>
                    </MenuItem>
                  </Select>
                )}
              />
            </FormControl>

            {motivoAcesso === "outro" && (
              <Controller
                name="outroMotivo"
                control={control}
                rules={{
                  required:
                    motivoAcesso === "outro"
                      ? "Por favor, especifique o motivo"
                      : false,
                }}
                render={({ field, fieldState: { error: fieldError } }) => (
                  <TextField
                    fullWidth
                    label="Especifique o motivo"
                    variant="outlined"
                    {...field}
                    error={!!fieldError}
                    helperText={fieldError ? fieldError.message : ""}
                    sx={{ mb: 2 }}
                  />
                )}
              />
            )}

            <Button
              fullWidth
              variant="contained"
              disabled={loading}
              color="primary"
              onClick={handleSubmit(handleVerifyCpf)}
              size={isMobile ? "medium" : "large"}
              sx={{ py: { xs: 1, sm: 1.5 } }}
              startIcon={
                loading ? <CircularProgress size={20} /> : <SearchIcon />
              }
            >
              {loading ? "Verificando..." : "Verificar"}
            </Button>
          </Box>
        ) : (
          <Box sx={{ mt: { xs: 2, sm: 3, md: 4 } }}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "center", sm: "flex-start" },
                  mb: 3,
                  gap: 3,
                }}
              >
                {/* Foto do pet */}
                <Avatar
                  src={
                    petData?.photo
                      ? `data:${petData.photo.contentType};base64,${petData.photo.data}`
                      : ""
                  }
                  sx={{
                    width: { xs: 120, sm: 150, md: 180 },
                    height: { xs: 120, sm: 150, md: 180 },
                    bgcolor: theme.palette.primary.main,
                    border: `3px solid ${theme.palette.primary.main}`,
                  }}
                >
                  {!petData?.photo && (
                    <PetsIcon sx={{ fontSize: { xs: 60, sm: 80, md: 100 } }} />
                  )}
                </Avatar>

                {/* Informações básicas */}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: "medium" }}
                  >
                    {petData?.nome || "Nome não disponível"}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    gutterBottom
                  >
                    ID: {petData?.id || "N/A"}
                  </Typography>
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <Typography>
                        <strong>Espécie:</strong> {petData?.especie || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography>
                        <strong>Raça:</strong> {petData?.raca || "N/A"}
                      </Typography>
                    </Grid>
                    {petData?.birthDate && (
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Idade:</strong>{" "}
                          {useFormatter.formatAge(petData.birthDate)} anos
                        </Typography>
                      </Grid>
                    )}
                    {petData?.birthDate && (
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Data de Nascimento:</strong>{" "}
                          {dayjs(petData.birthDate).format("DD/MM/YYYY")}
                        </Typography>
                      </Grid>
                    )}
                    {petData?.gender && (
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Gênero:</strong>{" "}
                          {petData.gender === "male" ? "Macho" : "Fêmea"}
                        </Typography>
                      </Grid>
                    )}
                    {petData?.genderStatus && (
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Status:</strong> {petData.genderStatus}
                        </Typography>
                      </Grid>
                    )}
                    {petData?.color && (
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Cor:</strong> {petData.color}
                        </Typography>
                      </Grid>
                    )}
                    {petData?.city && petData?.state && (
                      <Grid item xs={6}>
                        <Typography>
                          <strong>Localização:</strong> {petData.city}/
                          {petData.state}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Status do pet */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Status
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography>
                    <strong>Status do cadastro:</strong>{" "}
                    {petData?.active ? "Ativo" : "Inativo"}
                  </Typography>
                  {petData?.ultimaConsulta && (
                    <Typography>
                      <strong>Última Consulta:</strong> {petData.ultimaConsulta}
                    </Typography>
                  )}
                  {petData?.observacoes && (
                    <>
                      <Typography sx={{ mt: 1 }}>
                        <strong>Observações:</strong>
                      </Typography>
                      <Typography sx={{ mt: 0.5 }}>
                        {petData.observacoes}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Dados do tutor (apenas nome e telefone) */}
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <PersonIcon sx={{ mr: 1 }} /> Contato do Tutor
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: { xs: 1, sm: 2 },
                    mb: 2,
                    bgcolor: theme.palette.background.default,
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PersonIcon color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Nome
                          </Typography>
                          <Typography variant="body1">
                            {tutorData?.nome || "Nome não disponível"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PhoneIcon color="action" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Telefone
                          </Typography>
                          <Typography variant="body1">
                            {tutorData?.telefone || "Telefone não disponível"}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
}
